import {
  PRICING_PLAN_TIERS,
  isConsumerProPricingPlanTier,
} from '../constants/pricingPlans.constants'
import { en } from '../../i18n/translations/en'
import { getPricingCopy, type PricingCopyLabels } from './pricingI18n.helper'
import {
  CONSUMER_PRO_BILLING_INTERVAL,
  type ConsumerProBillingInterval,
} from '../constants/consumerProBilling.constants'
import { shouldShowHobbyOpenAppCta } from './pricingHobbyCta.helper'
import type { PricingPlanCard } from '../constants/pricingPlans.constants'

export type PricingCtaAction =
  | 'checkout'
  | 'consumer-checkout'
  | 'sign-in'
  | 'continue-app'
  | 'contact'
  | 'create-org'
  | 'coming-soon'
  | 'manage-consumer-billing'
  | 'manage-b2b-billing'
  | 'schedule-downgrade-to-monthly'
  | 'none'

export interface PricingCtaState {
  label: string
  action: PricingCtaAction
  disabled: boolean
  outline: boolean
}

export function isCurrentPlan(
  tier: string,
  orgPlanTier: string | null,
  consumerPlanTier: 'FREE' | 'PRO',
  consumerProBillingInterval: ConsumerProBillingInterval | null,
): boolean {
  if (tier === PRICING_PLAN_TIERS.ENTERPRISE && orgPlanTier === 'ENTERPRISE') return true
  if (tier === PRICING_PLAN_TIERS.BUSINESS && orgPlanTier === 'BUSINESS') return true
  if (tier === PRICING_PLAN_TIERS.HOBBY && consumerPlanTier === 'FREE') return true
  if (!isConsumerProPricingPlanTier(tier) || consumerPlanTier !== 'PRO') return false
  if (tier === PRICING_PLAN_TIERS.PRO_YEARLY) {
    return consumerProBillingInterval === CONSUMER_PRO_BILLING_INTERVAL.YEAR
  }
  // PRO_MONTHLY: treat unknown cadence (pre-sync) as monthly — historical default product.
  return (
    consumerProBillingInterval == null
    || consumerProBillingInterval === CONSUMER_PRO_BILLING_INTERVAL.MONTH
  )
}

function orgTierRank(tier: string | null): number {
  if (tier === 'STARTER') return 0
  if (tier === 'PRO') return 1
  if (tier === 'BUSINESS') return 2
  if (tier === 'ENTERPRISE') return 3
  return -1
}

function looksLikeStripeRecurringPriceId(id: string | null | undefined): boolean {
  if (id == null || typeof id !== 'string') return false
  const t = id.trim()
  return t.startsWith('price_') && t.length > 6
}

export function resolveCtaState(
  plan: PricingPlanCard,
  orgPlanTier: string | null,
  consumerPlanTier: 'FREE' | 'PRO',
  consumerProBillingInterval: ConsumerProBillingInterval | null,
  hasOrg: boolean,
  isSignedIn: boolean,
  accountAdminLoading: boolean,
  busyB2b: boolean,
  busyConsumer: boolean,
  stripeNegotiatedPriceId: string | null | undefined = null,
  isB2bOrgAdmin: boolean | undefined = false,
  /** When true, org has an active paid Stripe subscription (recurring); hide Enterprise "Contact us" CTA. */
  orgB2bStripeSubscriptionPaid: boolean | undefined = false,
  /** Current org maxSeats — used to determine upgrade vs manage for one-time Business plans. */
  orgMaxSeats: number | null | undefined = null,
  /** Selected bucket's maxSeats from the SeatBucketPicker — used to determine upgrade vs manage. */
  selectedBucketMaxSeats: number | null | undefined = null,
  copy: PricingCopyLabels = getPricingCopy(en),
): PricingCtaState {
  if (isConsumerProPricingPlanTier(plan.tier) && consumerPlanTier === 'PRO') {
    // A Pro entitlement with no recorded Stripe cadence came from a store rail (RevenueCat/Play/App
    // Store), not Stripe — the store grant sets planTier but never proBillingInterval. Such a user
    // must see "Manage" on BOTH Pro cards, never a Stripe checkout: offering one would charge them a
    // second time on a parallel billing rail neither side reconciles. (Routing Manage to the correct
    // rail is handled separately via proProvider — see specs/cross-platform-pro.) Without this, the
    // Yearly card falls through to `consumer-checkout` because isCurrentPlan treats a null cadence as
    // monthly, so only the Monthly card is "current".
    if (consumerProBillingInterval == null) {
      return {
        label: copy.manageSubscription,
        action: 'manage-consumer-billing',
        disabled: busyConsumer,
        outline: true,
      }
    }
    if (isCurrentPlan(plan.tier, orgPlanTier, consumerPlanTier, consumerProBillingInterval)) {
      return {
        label: copy.manageSubscription,
        action: 'manage-consumer-billing',
        disabled: busyConsumer,
        outline: true,
      }
    }
    // Yearly Pro subscriber viewing the Monthly card: open the Stripe Customer Portal
    // so they can switch cadence with prorated credit — same pattern as B2B self-serve.
    if (
      consumerProBillingInterval === CONSUMER_PRO_BILLING_INTERVAL.YEAR
      && plan.consumerProBillingInterval === CONSUMER_PRO_BILLING_INTERVAL.MONTH
    ) {
      return {
        label: copy.switchToMonthly,
        action: 'schedule-downgrade-to-monthly',
        disabled: false,
        outline: true,
      }
    }
  }

  // Org is already on Enterprise: "Your plan" comes from isCurrentPlan on the card.
  // "Contact us" only when there is no active org Stripe subscription (e.g. one-time / quote path).
  if (
    plan.tier === PRICING_PLAN_TIERS.ENTERPRISE
    && orgPlanTier === 'ENTERPRISE'
    && plan.contactSales
    && !plan.comingSoon
  ) {
    if (orgB2bStripeSubscriptionPaid === true) {
      return { label: '', action: 'none', disabled: true, outline: false }
    }
    return { label: plan.ctaLabel, action: 'contact', disabled: false, outline: true }
  }

  if (plan.comingSoon) {
    const existingBusinessKeepsSelfServe =
      plan.tier === PRICING_PLAN_TIERS.BUSINESS && orgPlanTier === 'BUSINESS' && hasOrg
    if (!existingBusinessKeepsSelfServe) {
      // Surface the "Coming soon" status directly in the CTA button (disabled,
      // non-interactive) instead of opening a modal explaining the same thing.
      const label =
        plan.tier === PRICING_PLAN_TIERS.BUSINESS
          ? copy.comingSoonInProduction
          : copy.comingSoonBadge
      return {
        label,
        action: 'coming-soon',
        disabled: true,
        outline: false,
      }
    }
  }

  if (plan.consumerProCheckout) {
    if (!isSignedIn) {
      return {
        label: copy.signInToSubscribe,
        action: 'sign-in',
        disabled: false,
        outline: true,
      }
    }
    return { label: plan.ctaLabel, action: 'consumer-checkout', disabled: busyConsumer, outline: true }
  }

  if (
    plan.tier === PRICING_PLAN_TIERS.ENTERPRISE
    && plan.contactSales
    && hasOrg
    && looksLikeStripeRecurringPriceId(stripeNegotiatedPriceId)
  ) {
    if (!isB2bOrgAdmin) {
      return {
        label: copy.enterpriseCheckoutAdminOnly,
        action: 'none',
        disabled: true,
        outline: false,
      }
    }
    const isDowngrade = orgTierRank(PRICING_PLAN_TIERS.ENTERPRISE) <= orgTierRank(orgPlanTier)
    if (isDowngrade) {
      return { label: plan.ctaLabel, action: 'none', disabled: true, outline: false }
    }
    return {
      label: copy.enterpriseSubscribe,
      action: 'checkout',
      disabled: busyB2b,
      outline: true,
    }
  }

  if (plan.contactSales) {
    return { label: plan.ctaLabel, action: 'contact', disabled: false, outline: true }
  }

  if (plan.requiresOrg && !hasOrg) {
    return { label: copy.createOrgCta, action: 'create-org', disabled: false, outline: true }
  }

  if (plan.requiresOrg && hasOrg) {
    const isDowngrade = orgTierRank(plan.tier) <= orgTierRank(orgPlanTier)

    // Business card, org has no paid plan yet → Subscribe (start one-time checkout).
    if (
      plan.tier === PRICING_PLAN_TIERS.BUSINESS
      && (orgPlanTier == null || orgPlanTier === 'STARTER')
    ) {
      return {
        label: copy.businessSubscribe,
        action: 'checkout',
        disabled: busyB2b,
        outline: true,
      }
    }

    // One-time Business plan: Upgrade (higher bucket), Manage (current bucket), disabled (lower).
    if (
      plan.tier === PRICING_PLAN_TIERS.BUSINESS
      && orgPlanTier === 'BUSINESS'
      && !orgB2bStripeSubscriptionPaid
    ) {
      const hasSeats = typeof selectedBucketMaxSeats === 'number' && typeof orgMaxSeats === 'number'
      const canUpgrade = hasSeats && selectedBucketMaxSeats > orgMaxSeats
      const isCurrent = hasSeats && selectedBucketMaxSeats === orgMaxSeats
      if (canUpgrade) {
        return {
          label: copy.upgradeSeatBucket,
          action: 'checkout',
          disabled: busyB2b,
          outline: true,
        }
      }
      if (isCurrent) {
        return {
          label: copy.managePlan,
          action: 'manage-b2b-billing',
          disabled: false,
          outline: true,
        }
      }
      // Lower than current — can't downgrade.
      return { label: '', action: 'none', disabled: true, outline: false }
    }

    if (isDowngrade) {
      return { label: plan.ctaLabel, action: 'none', disabled: true, outline: false }
    }
    return { label: plan.ctaLabel, action: 'checkout', disabled: busyB2b, outline: true }
  }

  if (plan.tier === PRICING_PLAN_TIERS.HOBBY) {
    if (!isSignedIn) {
      return {
        label: copy.logIn,
        action: 'sign-in',
        disabled: false,
        outline: true,
      }
    }
    if (accountAdminLoading || !shouldShowHobbyOpenAppCta(consumerPlanTier, orgPlanTier)) {
      return { label: '', action: 'none', disabled: true, outline: false }
    }
    return {
      label: plan.ctaLabel,
      action: 'continue-app',
      disabled: false,
      outline: true,
    }
  }

  return { label: plan.ctaLabel, action: 'none', disabled: true, outline: false }
}
